/**
 * Which entities pull the light out of a room, how hard, and how far ahead of
 * themselves.
 *
 * This is an *atmosphere* property, not a behaviour, so it lives in the render
 * layer rather than in `EntityDef`: nothing in `entities/` needs to know that a
 * smiler is followed around by a hole in the ceiling lighting, and the number
 * wants to be tuned by looking at the screen.
 *
 * The intent is that the dark arrives *before* the thing does, and it does —
 * but by much less than the radius suggests, so do not read 34 m as 34 m of
 * warning. Measured on Level 0, down a lit hall, against a clean frame:
 *
 *   smiler at   the picture              the body
 *   36 m        no visible change        invisible
 *   30 m        ceiling panels out       3 px, invisible
 *   26 m        clearly darker           67 px, a speck
 *   22 m        76% of the frame         125 px, a shape
 *
 * Two things eat the rest. The smoothstep in `at` gives away most of the outer
 * half of the radius — a fixture 24 m from the source is dimmed 20%, which is
 * not something a player sees — so the visible edge of the dark sits around
 * 20 m out, not 34. And Level 0's fog is already thick at 40 m, so the panels
 * that *are* out at that range barely move a pixel.
 *
 * What is left is a real but small lead: a few seconds of "the lights ahead are
 * going" before the shape resolves. Widening it means a bigger radius *and* a
 * flatter falloff, and both change how much of the level is dark at once — a
 * feel decision, not a bug. See `TUNING.render.lightDrainRadius`.
 *
 * The field itself is tiny: `entityBudget` is 0–3 on every level in the game,
 * so `sync` rebuilds it from scratch each frame and `at` is a loop over at most
 * `MAX_SOURCES` entries. It is the *fixtures* that are numerous, and those are
 * walked from the light rig's existing spatial buckets rather than from here.
 */

import { TUNING } from '../core/config';
import { clamp01, smoothstep } from '../core/math';
import type { EntityKind, EntityMode, EntityState, LevelSpec } from '../core/types';

/**
 * How hard each kind pulls the room down, 0..1. Absent means "not at all",
 * which is most of the roster on purpose: a thing every monster does is not a
 * signal, it is weather.
 *
 * - `smiler` — canonically lives in the dark and retreats from light. A smiler
 *   that brings its own dark is the version of it everyone already imagines.
 * - `faceling` — only closes while unobserved, so darkening the room it is
 *   crossing is frightening *and* mechanically honest: it really is harder to
 *   observe.
 * - `bacteria` — Level 0's own, documented as a dark figure. Moderate, because
 *   it is the common one and it screams anyway.
 *
 * Deliberately excluded: `hound` is blind and hunts by sound, and the players
 * like it as it is. `partygoer` is excluded for the opposite reason — the
 * Funrooms' horror is that they are far too *bright*, and draining that would
 * swap a specific effect for a generic one. `deathmoth` is drawn to light,
 * `clump` does not move, and `hollow` is not there.
 */
const DRAIN_BY_KIND: Partial<Record<EntityKind, number>> = {
  smiler: 1,
  faceling: 0.82,
  bacteria: 0.55,
};

/**
 * Mode multiplier. Something parked at the far end of the level still dims its
 * own corner — that is a landmark and it is worth having — but the full effect
 * belongs to a thing that is doing something.
 */
const MODE_SCALE: Partial<Record<EntityMode, number>> = {
  dormant: 0.6,
  dead: 0,
};

/** Hard cap on simultaneous drainers. Levels cap `entityBudget` well below it. */
const MAX_SOURCES = 8;

/**
 * Radius as a fraction of the base, per unit of drain amount. A weaker drainer
 * is both shallower and tighter, so the three kinds are told apart by the size
 * of the dark as well as its depth.
 */
function radiusFor(amount: number): number {
  return TUNING.render.lightDrainRadius * (0.62 + 0.38 * amount);
}

/**
 * How much of the effect a level gets at all.
 *
 * Two gates, both read off the level's own spec so a new level answers them
 * without being listed anywhere:
 *
 * - **Ambient.** Draining light out of a level that has none is not a scare,
 *   it is a black screen. Level 6 (Lights Out) is pitch dark by design at 0.03
 *   and comes out at zero — the effect is suppressed entirely there, which is
 *   right: on that level a light in the distance is the exit, and putting it
 *   out would delete the only navigation the player has.
 * - **Fixture density.** A level whose lights are 420 m apart has nothing to
 *   put out. This also catches Lights Out a second time, and it keeps the
 *   effect honest anywhere else that goes sparse.
 *
 * Level 2 (Pipe Dreams) lands around half, which is the intent: it is already
 * the darkest walkable level in the game and a full-strength drain there would
 * cross from tense into unplayable.
 */
function levelScale(spec: LevelSpec): number {
  const ambient = smoothstep(0.06, 0.22, spec.ambientIntensity);
  const density = smoothstep(140, 45, spec.lightSpacing);
  return clamp01(ambient * density);
}

/**
 * The live set of light-draining entities, as flat arrays the light rig can
 * walk without touching objects.
 *
 * `count` is the only mutable part of the contract; the arrays are `MAX_SOURCES`
 * long and only the first `count` entries are meaningful.
 */
export interface LightDrainField {
  /** Number of live drainers this frame. 0 on levels where the effect is off. */
  readonly count: number;
  /** How much of the effect this level gets, 0..1. 0 means permanently off. */
  readonly scale: number;
  /** Largest radius any source can have here, for the fixture bucket walk. */
  readonly maxRadius: number;
  readonly sx: Float32Array;
  readonly sz: Float32Array;
  /** Falloff radius in metres, per source. */
  readonly sr: Float32Array;
  /** Peak drain at the source's own position, 0..1. */
  readonly sa: Float32Array;
  /** Rebuild from this frame's entity list. Allocates nothing. */
  sync(entities: readonly EntityState[]): void;
  /** Drain at a world point, 0..1. 0 is untouched, 1 is as dark as it goes. */
  at(wx: number, wz: number): number;
}

/** Build the per-level drain field. Cheap; one per level build. */
export function makeLightDrainField(spec: LevelSpec): LightDrainField {
  const scale = levelScale(spec);
  const sx = new Float32Array(MAX_SOURCES);
  const sz = new Float32Array(MAX_SOURCES);
  const sr = new Float32Array(MAX_SOURCES);
  const sa = new Float32Array(MAX_SOURCES);
  /** Sort key for the keep-the-nearest cull. */
  const sd = new Float32Array(MAX_SOURCES);
  const maxRadius = scale > 0 ? radiusFor(1) : 0;
  let count = 0;

  return {
    get count(): number {
      return count;
    },
    scale,
    maxRadius,
    sx,
    sz,
    sr,
    sa,

    sync(entities: readonly EntityState[]): void {
      count = 0;
      if (scale <= 0) return;
      for (const e of entities) {
        const kindAmount = DRAIN_BY_KIND[e.kind];
        if (kindAmount === undefined) continue;
        const amount = kindAmount * (MODE_SCALE[e.mode] ?? 1) * scale;
        if (amount <= 0.01) continue;

        // Nearest-first insertion, same shape as the light rig's fixture pick:
        // if the level ever runs more drainers than slots, the ones the player
        // is standing among are the ones that matter.
        const d = e.distToPlayer;
        if (count >= MAX_SOURCES && d >= sd[MAX_SOURCES - 1]!) continue;
        let j = count < MAX_SOURCES ? count++ : MAX_SOURCES - 1;
        while (j > 0 && sd[j - 1]! > d) {
          sd[j] = sd[j - 1]!;
          sx[j] = sx[j - 1]!;
          sz[j] = sz[j - 1]!;
          sr[j] = sr[j - 1]!;
          sa[j] = sa[j - 1]!;
          j--;
        }
        sd[j] = d;
        sx[j] = e.pos.x;
        sz[j] = e.pos.z;
        sr[j] = radiusFor(amount);
        sa[j] = amount;
      }
    },

    at(wx: number, wz: number): number {
      let worst = 0;
      for (let i = 0; i < count; i++) {
        const r = sr[i]!;
        const dx = wx - sx[i]!;
        const dz = wz - sz[i]!;
        const d2 = dx * dx + dz * dz;
        if (d2 >= r * r) continue;
        // Smoothstep rather than the inverse-square the real lights use: the
        // outer third has to be worth something or the dark never leads.
        const t = 1 - Math.sqrt(d2) / r;
        const v = sa[i]! * t * t * (3 - 2 * t);
        if (v > worst) worst = v;
      }
      // Max, not sum. Two of them in one corridor is not darker than black, and
      // keeping it a max is what makes the per-kind numbers mean anything.
      return worst;
    },
  };
}
